import { create } from 'zustand';
import {
  api, setAccessToken, setIdleTimeoutMs, onAuthFailure, broadcastAuthChange, onCrossTabSignIn,
  EMBEDDED, getStoredRefreshToken, clearStoredRefreshToken,
} from '@/lib/api';

// Route navigation that works under both routers: the web build uses
// BrowserRouter (path-based); the native-app-shell embedded build uses
// HashRouter (routes live in location.hash), so hard navigations must target
// the fragment or they hit a path the shell's static server doesn't serve.
function currentAppRoute(): { path: string; search: string } {
  if (EMBEDDED) {
    const h = window.location.hash.replace(/^#/, '') || '/';
    const q = h.indexOf('?');
    return q >= 0 ? { path: h.slice(0, q), search: h.slice(q) } : { path: h, search: '' };
  }
  return { path: window.location.pathname, search: window.location.search };
}
function hardNavigate(route: string) {
  if (EMBEDDED) window.location.hash = `#${route.startsWith('/') ? route : `/${route}`}`;
  else window.location.href = route;
}

/** Sanitize a `?next=` redirect target (pentest A-05: open redirect). Only a
 *  same-origin ABSOLUTE PATH is allowed — one that starts with a single `/`.
 *  Rejects absolute URLs (`https://evil.com`), protocol-relative (`//evil.com`),
 *  backslash tricks (`/\evil.com`, which browsers treat as `//`), and non-path
 *  schemes (`javascript:`, `data:`) — so a crafted `next` can't bounce the user
 *  off-site or execute a `javascript:` URI. Falls back to the app root. */
export function safeInternalPath(raw: string | null | undefined): string {
  if (!raw) return '/';
  const s = Array.from(raw).filter((c) => { const n = c.charCodeAt(0); return n > 31 && n !== 127; }).join(''); // drop control chars browsers ignore in URLs
  return /^\/(?![/\\])/.test(s) ? s : '/';
}

/** Pull the admin-configurable idle timeout from app-settings and apply it
 *  to the active session. Fails open — if the setting is missing or the
 *  endpoint errors, the existing default in api.ts (30 minutes) stays in
 *  effect. Called after each /auth/me so every tab picks up admin edits
 *  on its next session refresh. */
async function applyIdleTimeoutFromSettings(): Promise<void> {
  try {
    const r = await api<any[]>('/master-data/app-settings?search=session.idleTimeoutMinutes');
    const row = (r.data || []).find((s: any) => s.key === 'session.idleTimeoutMinutes');
    const mins = Number(row?.value);
    if (Number.isFinite(mins) && mins >= 0 && mins <= 24 * 60) {
      setIdleTimeoutMs(mins * 60 * 1000);
    }
  } catch { /* keep the default */ }
}
import { initPushRegistration, teardownPushRegistration } from '@/features/notifications/push';
import type { EmployeeSummary, AuthMode } from '@atlas/shared';

export interface AuthState {
  employee: EmployeeSummary | null;
  authMode: AuthMode;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: any[];

  // Actions
  login: (email: string, password: string) => Promise<void>;
  requestMagicLink: (email: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  fetchAuthMode: () => Promise<void>;
  setEmployee: (employee: EmployeeSummary | null) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  employee: null,
  authMode: 'both',
  isAuthenticated: false,
  isLoading: true,
  permissions: [],

  login: async (email: string, password: string) => {
    const res = await api<{ accessToken: string; employee: EmployeeSummary }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (res.success && res.data) {
      setAccessToken(res.data.accessToken);
      // Don't flip `isAuthenticated: true` here — let `fetchMe` set it
      // ATOMICALLY with `permissions`. Otherwise the login page's redirect
      // useEffect fires the moment isAuthenticated is true but permissions
      // is still empty, and `useDefaultHomePath` falls through to /profile.
      set({ employee: res.data.employee });
      await get().fetchMe();
      // Notify other tabs of this origin that a sign-in just happened so
      // they can pick up the new session from the shared refresh cookie.
      broadcastAuthChange('sign-in');
      void initPushRegistration();
    } else {
      throw new Error(res.error?.message || 'Login failed');
    }
  },

  requestMagicLink: async (email: string) => {
    const res = await api('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    if (!res.success) {
      throw new Error(res.error?.message || 'Failed to send magic link');
    }
  },

  verifyMagicLink: async (token: string) => {
    const res = await api<{ accessToken: string; employee: EmployeeSummary }>(
      `/auth/magic-link/verify?token=${encodeURIComponent(token)}`
    );
    if (res.success && res.data) {
      setAccessToken(res.data.accessToken);
      // Same reason as `login`: defer the `isAuthenticated: true` flip to
      // `fetchMe` so it lands atomically with `permissions`.
      set({ employee: res.data.employee });
      await get().fetchMe();
      broadcastAuthChange('sign-in');
      void initPushRegistration();
    } else {
      throw new Error(res.error?.message || 'Invalid or expired link');
    }
  },

  logout: async () => {
    // Embedded clients have no cookie to identify the session, so send the
    // refresh token in the body for server-side revocation.
    await api('/auth/logout', {
      method: 'POST',
      ...(EMBEDDED ? { body: JSON.stringify({ refreshToken: getStoredRefreshToken() }) } : {}),
    }).catch(() => {});
    await teardownPushRegistration();
    setAccessToken(null);
    clearStoredRefreshToken();
    set({ employee: null, isAuthenticated: false, permissions: [] });
    // Other tabs of the same origin pick this up and clear their own state.
    broadcastAuthChange('sign-out');
  },

  fetchMe: async () => {
    set({ isLoading: true });
    try {
      const res = await api<any>('/auth/me');
      if (res.success && res.data) {
        const data = res.data;
        set({
          employee: {
            id: data.id,
            email: data.email,
            fullName: data.fullName,
            rolePermission: data.rolePermission
              ? { id: data.rolePermission.id, name: data.rolePermission.name }
              : null,
            profileImage: data.profileImage,
          },
          permissions: data.rolePermission?.permissions || [],
          isAuthenticated: true,
          isLoading: false,
        });
        // Apply the admin-configurable idle timeout AFTER auth is in place
        // so the request carries the bearer token. Failure here keeps the
        // built-in 30-minute default.
        void applyIdleTimeoutFromSettings();
      } else {
        set({ isAuthenticated: false, isLoading: false });
      }
    } catch {
      set({ isAuthenticated: false, isLoading: false });
    }
  },

  fetchAuthMode: async () => {
    try {
      const res = await api<{ mode: AuthMode }>('/auth/auth-mode');
      if (res.success && res.data) {
        set({ authMode: res.data.mode });
      }
    } catch {
      // Default to 'both' on error
    }
  },

  setEmployee: (employee) => set({ employee, isAuthenticated: !!employee }),
}));

// Register a one-shot listener so /auth/refresh failures clear local state
// and bounce the user to /auth/login. Without this, a failed refresh leaves
// the UI authenticated-but-empty and the user has to manually reload.
onAuthFailure(() => {
  useAuthStore.setState({
    employee: null,
    isAuthenticated: false,
    permissions: [],
  });
  // The session ended WITHOUT an explicit logout — expired/revoked refresh
  // token, idle timeout, or a cross-tab sign-out. `logout()` unregisters the
  // push token itself, but this path never ran that, so the DeviceToken row
  // would survive and the server would keep pushing to a signed-out phone.
  // Tear it down here too. Best-effort and fire-and-forget: the DELETE is now
  // possession-based server-side, which matters because the access token has
  // already been cleared by the time we reach this handler.
  void teardownPushRegistration();
  // Preserve the route the user was on so the login page can bounce them back
  // after they reauthenticate. Router-aware (path vs hash) via currentAppRoute.
  if (typeof window !== 'undefined') {
    const { path, search } = currentAppRoute();
    if (!path.startsWith('/auth/')) {
      const next = encodeURIComponent(path + search);
      hardNavigate(`/auth/login?next=${next}`);
    }
  }
});

// Cross-tab sign-in: another tab just logged the user in. If we're sitting
// on /auth/login (or otherwise unauthenticated), pull /auth/me via the
// shared refresh cookie so this tab mirrors the new session immediately.
//
// MUST `await fetchMe` before the redirect — otherwise the hard-nav races
// the in-flight /auth/refresh + /auth/me chain and the new page boots
// with `sessionStorage` still empty, which under bad timing leaves the
// tab stranded on the login form even though a fresh session exists.
// Waiting for the chain guarantees the access token is in sessionStorage
// before the navigation reads it.
onCrossTabSignIn(async () => {
  // Skip if we're already authenticated — avoids a redundant fetch.
  if (useAuthStore.getState().isAuthenticated) return;
  await useAuthStore.getState().fetchMe();
  // Bail if the refresh cookie ended up not being usable (e.g. server
  // rejected it). Leaves the user on the login form rather than bouncing
  // them somewhere they can't reach.
  if (!useAuthStore.getState().isAuthenticated) return;
  // If we landed here from /auth/login, bounce home (or back to ?next).
  if (typeof window !== 'undefined') {
    const { path, search } = currentAppRoute();
    if (path.startsWith('/auth/login')) {
      // A-05: validate `next` — only a same-origin path is allowed, never an
      // absolute/protocol-relative URL or `javascript:` scheme.
      const next = safeInternalPath(new URLSearchParams(search).get('next'));
      hardNavigate(next);
    }
  }
});
